const fieldSources = [
  {
    number: "01",
    title: "Invoice",
    detail: "What was charged",
  },
  {
    number: "02",
    title: "Municipal rule",
    detail: "How it was priced",
  },
  {
    number: "03",
    title: "Laboratory sample",
    detail: "What was measured",
  },
  {
    number: "04",
    title: "Plant event",
    detail: "What was happening",
  },
] as const;

export function FieldEvidenceMap() {
  return (
    <figure
      className="field-map"
      aria-label="A wastewater invoice, municipal rule, laboratory sample, and plant event enter the Kratis decision engine, which supports an independent call, a customer action, and a verified result."
    >
      <header className="field-map__header">
        <div>
          <p className="mini-label">Wastewater cost review</p>
          <h2>One increase. Four views of what changed</h2>
        </div>
        <span>
          <i aria-hidden="true" />
          Source-linked
        </span>
      </header>

      <div className="field-map__sources">
        {fieldSources.map((source) => (
          <article key={source.title}>
            <span>{source.number}</span>
            <strong>{source.title}</strong>
            <small>{source.detail}</small>
          </article>
        ))}
      </div>

      <div className="field-map__merge" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>

      <div className="field-map__core">
        <div className="field-map__mark" aria-hidden="true">
          K
        </div>
        <div>
          <span>KRATIS DECISION ENGINE</span>
          <strong>Matched by facility · date · operating condition</strong>
        </div>
        <p>Likely cause · Missing fact · Confidence</p>
      </div>

      <div className="field-map__result">
        <article>
          <span>01</span>
          <strong>Independent call</strong>
          <small>What to do</small>
        </article>
        <i aria-hidden="true">→</i>
        <article>
          <span>02</span>
          <strong>Customer action</strong>
          <small>What management chose</small>
        </article>
        <i aria-hidden="true">→</i>
        <article>
          <span>03</span>
          <strong>Verified result</strong>
          <small>What changed</small>
        </article>
      </div>

      <div className="field-map__return" aria-hidden="true">
        <span>↳</span>
        <i />
      </div>

      <figcaption>
        With customer permission, each verified result can improve the next
        similar decision
      </figcaption>
    </figure>
  );
}

const learningStages = [
  {
    number: "01",
    title: "Frontline evidence",
    detail: "What is happening",
  },
  {
    number: "02",
    title: "Focused models",
    detail: "Cause · next fact · likely action",
  },
  {
    number: "03",
    title: "Clear decision",
    detail: "Sources · value · confidence",
  },
  {
    number: "04",
    title: "Customer action",
    detail: "What management chooses",
  },
  {
    number: "05",
    title: "Verified result",
    detail: "What changed",
  },
] as const;

export function IntelligenceLoop() {
  return (
    <figure
      className="learning-loop"
      aria-label="Permissioned frontline evidence supports focused models, a clear decision, customer action, and a verified result. The verified result improves the next similar decision."
    >
      <div className="learning-loop__stages">
        {learningStages.map((stage, index) => (
          <article
            className={stage.title === "Focused models" ? "is-core" : ""}
            key={stage.title}
          >
            <span>{stage.number}</span>
            <strong>{stage.title}</strong>
            <small>{stage.detail}</small>
            {index < learningStages.length - 1 ? (
              <i aria-hidden="true">→</i>
            ) : null}
          </article>
        ))}
      </div>
      <div className="learning-loop__return">
        <svg viewBox="0 0 1100 70" aria-hidden="true">
          <path d="M1044 8C1044 58 850 58 550 58S56 58 56 8" />
          <path d="m46 18 10-10 10 10" />
        </svg>
        <span>The result becomes evidence for the next decision</span>
      </div>
    </figure>
  );
}
